/**
 * StaffCustomers — IT-SCE Intern: Personal-Users (Keycloak) verwalten.
 *
 * TQ #9694 (Staff-Migration Teil A, Bundle #9523). Portiert aus
 * customer-portal/src/pages/staff/StaffCustomers.tsx — gleiche
 * API-Calls (api.staffListCustomers/staffEnableCustomer/…), Styling
 * an StaffKnowledge.tsx (org-portal, TQ #7128) angeglichen (inline
 * styles, schlanke Shell ohne CockpitApp-Layout, "← Cockpit"-Link).
 *
 * Routing: outer-router-level (App.tsx) unter /staff/customers, hinter
 * StaffOnly-Guard, gerendert über StaffCustomersHub (Tab "Kunden").
 */

import { useEffect, useState } from 'react'
import { api, StaffCustomer } from '../../services/api'
import { useToast } from '../../components/ui'

export default function StaffCustomers() {
  const [customers, setCustomers] = useState<StaffCustomer[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<'all' | 'enabled' | 'disabled'>('all')
  const { showToast } = useToast()

  async function load() {
    setLoading(true)
    try {
      const resp = await api.staffListCustomers()
      setCustomers(resp.customers ?? [])
    } catch (err) {
      const msg = err instanceof Error ? err.message : String((err as { error?: string })?.error ?? err)
      showToast('error', `Laden fehlgeschlagen: ${msg}`)
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function toggle(c: StaffCustomer) {
    try {
      if (c.enabled) await api.staffDisableCustomer(c.id)
      else await api.staffEnableCustomer(c.id)
      showToast('success', `${c.email}: ${c.enabled ? 'deaktiviert' : 'aktiviert'}`)
      load()
    } catch (err) {
      const msg = err instanceof Error ? err.message : String((err as { error?: string })?.error ?? err)
      showToast('error', `Fehler: ${msg}`)
    }
  }

  async function resetPw(c: StaffCustomer) {
    if (!window.confirm(`Passwort-Reset Email an ${c.email} senden?`)) return
    try {
      await api.staffResetCustomerPassword(c.id)
      showToast('success', 'Reset-Email gesendet')
    } catch (err) {
      const msg = err instanceof Error ? err.message : String((err as { error?: string })?.error ?? err)
      showToast('error', `Fehler: ${msg}`)
    }
  }

  const filtered = customers.filter(c => {
    if (filter === 'enabled' && !c.enabled) return false
    if (filter === 'disabled' && c.enabled) return false
    if (search) {
      const q = search.toLowerCase()
      return (c.email + ' ' + c.username + ' ' + (c.firstName ?? '') + ' ' + (c.lastName ?? '')).toLowerCase().includes(q)
    }
    return true
  })

  return (
    <div>
      <p style={{ color: '#666', marginTop: 0, marginBottom: '1rem', fontSize: '0.9rem' }}>
        Personal-Users in Keycloak ({customers.length})
      </p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
        <input
          type="search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Suchen..."
          style={{ flex: 1, padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 6 }}
        />
        {(['all', 'enabled', 'disabled'] as const).map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            style={{
              padding: '8px 14px',
              background: filter === f ? '#FF6B35' : '#fff',
              color: filter === f ? '#fff' : '#374151',
              border: '1px solid #e5e7eb',
              borderRadius: 999,
              cursor: 'pointer',
              fontSize: 13,
            }}
          >
            {f === 'all' ? `Alle (${customers.length})` : f === 'enabled' ? 'Aktiv' : 'Deaktiviert'}
          </button>
        ))}
      </div>

      {loading ? (
        <p style={{ color: '#666' }}>Lade Kunden…</p>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead style={{ background: '#f9fafb' }}>
              <tr>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Email / Name</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>ERPNext-Customer</th>
                <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>Subs</th>
                <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>€/Monat</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Status</th>
                <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => (
                <tr key={c.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '10px 12px' }}>
                    <div style={{ fontWeight: 500 }}>{c.email}</div>
                    <div style={{ color: '#6b7280', fontSize: 12 }}>
                      {[c.firstName, c.lastName].filter(Boolean).join(' ') || c.username}
                    </div>
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    {c.isEmployee && (
                      <span style={{
                        display: 'inline-block',
                        padding: '2px 8px',
                        borderRadius: 12,
                        fontSize: 11,
                        background: '#dbeafe',
                        color: '#1e40af',
                        marginRight: 6,
                      }}>
                        👷 Mitarbeiter
                      </span>
                    )}
                    {c.erpnextCustomerId ? (
                      <code style={{ fontSize: 12, color: '#374151' }}>{c.erpnextCustomerId}</code>
                    ) : (
                      <span style={{ color: '#f59e0b', fontSize: 12 }}>nicht verknüpft</span>
                    )}
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'right' }}>{c.activeServices ?? 0}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                    {(c.monthlySpend ?? 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <span style={{
                      padding: '2px 8px',
                      borderRadius: 12,
                      fontSize: 12,
                      background: c.enabled ? '#d1fae5' : '#fee2e2',
                      color: c.enabled ? '#065f46' : '#991b1b',
                    }}>
                      {c.enabled ? 'Aktiv' : 'Deaktiviert'}
                    </span>
                    {!c.emailVerified && (
                      <span style={{ marginLeft: 6, padding: '2px 8px', borderRadius: 12, fontSize: 11, background: '#fef3c7', color: '#92400e' }}>
                        Email unverified
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button
                      onClick={() => toggle(c)}
                      style={{ padding: '4px 10px', marginRight: 4, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}
                    >
                      {c.enabled ? 'Deaktivieren' : 'Aktivieren'}
                    </button>
                    <button
                      onClick={() => resetPw(c)}
                      style={{ padding: '4px 10px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}
                    >
                      Passwort-Reset
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ padding: 32, textAlign: 'center', color: '#9ca3af' }}>
                    Keine Kunden gefunden.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
